// =====================================================================
// Faz AN (2026-05-07) — Subscription / Repeat Order Yönetimi
// =====================================================================
// Admin müşteriler için düzenli sipariş abonelikleri oluşturur:
//   "Her 30 günde bir 100 koli kapak otomatik sipariş ver"
//
// Worker (subscription-runner.js) saatte 1 due'ları işler. Bu sayfa CRUD.
//
// Endpoint:
//   GET  /api/admin/subscriptions
//   POST /api/admin/subscriptions
//   PUT  /api/admin/subscriptions/:id (update items/frequency/notes)
//   PUT  /api/admin/subscriptions/:id/toggle (active ↔ paused)
//   DELETE /api/admin/subscriptions/:id (soft cancel)
//   POST /api/admin/subscriptions/:id/run-now (manuel tetik)
// =====================================================================
import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Card, Typography, Grid, Chip, Table, TableHead, TableRow, TableCell,
  TableBody, CircularProgress, Alert, IconButton, Tooltip, Button, Switch,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Autocomplete,
  MenuItem
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RepeatIcon from "@mui/icons-material/Repeat";
import API from "../services/api";
import toast from "react-hot-toast";

const fmtMoney = (v) => Number(v || 0).toLocaleString("tr-TR", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + " ₺";
const fmtDate = (s) => s ? new Date(s).toLocaleDateString("tr-TR", {
  day: "2-digit", month: "short", year: "numeric"
}) : "—";

// Yaygın frequency hızlı seçim — owner kolaylık için
const FREQUENCY_PRESETS = [
  { days: 7, label: "Her hafta" },
  { days: 14, label: "2 haftada bir" },
  { days: 30, label: "Aylık" },
  { days: 60, label: "2 ayda bir" },
  { days: 90, label: "3 ayda bir (çeyreklik)" }
];

// Status → renkli chip
const STATUS_META = {
  active:    { color: "#10b981", label: "Aktif" },
  paused:    { color: "#f59e0b", label: "Duraklatıldı" },
  cancelled: { color: "#94a3b8", label: "İptal" }
};

export default function Subscriptions() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  // Lookups (form için)
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);

  // New subscription dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({
    user_id: null, frequency_days: 30,
    next_run_at: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10),
    notes: "",
    items: [{ product_id: null, quantity: 1, price: 0 }]
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [subsR, usersR, productsR] = await Promise.allSettled([
        API.get("/api/admin/subscriptions"),
        API.get("/api/users"),
        API.get("/api/admin/products?limit=500")
      ]);
      // Server response shape'leri farklı olabilir (array vs {data:[]} vs {items:[]})
      // — TypeError 'c is not a function' (find/map) önlemek için her zaman Array'e zorla.
      const toArr = (raw) => {
        if (Array.isArray(raw)) return raw;
        if (Array.isArray(raw?.data)) return raw.data;     // /api/admin/products → {data, total}
        if (Array.isArray(raw?.items)) return raw.items;   // /api/admin/subscriptions → {items}
        return [];
      };
      setSubs(subsR.status === "fulfilled" ? toArr(subsR.value.data) : []);
      setUsers(usersR.status === "fulfilled" ? toArr(usersR.value.data) : []);
      setProducts(productsR.status === "fulfilled" ? toArr(productsR.value.data) : []);
      setErr(null);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };
  // async load → cleanup'ta Promise çağrılmasın (arrow wrap)
  useEffect(() => { load(); }, []);

  // Form yardımcıları
  const addFormItem = () => setForm({
    ...form,
    items: [...form.items, { product_id: null, quantity: 1, price: 0 }]
  });
  const removeFormItem = (i) => setForm({
    ...form, items: form.items.filter((_, idx) => idx !== i)
  });
  const updateFormItem = (i, field, value) => {
    const newItems = [...form.items];
    newItems[i] = { ...newItems[i], [field]: value };
    // Ürün seçildiğinde fiyatı otomatik doldur (kullanıcı manuel değiştirebilir)
    if (field === "product_id" && value) {
      const p = products.find(x => x.id === value);
      if (p && !newItems[i].price) newItems[i].price = Number(p.price) || 0;
    }
    setForm({ ...form, items: newItems });
  };

  const submitCreate = async () => {
    if (!form.user_id) { toast.error("Müşteri seç"); return; }
    if (form.items.length === 0 || form.items.some(it => !it.product_id || it.quantity <= 0)) {
      toast.error("Tüm ürün ve miktar dolu olmalı");
      return;
    }
    setSaving(true);
    try {
      await API.post("/api/admin/subscriptions", form);
      toast.success("Abonelik oluşturuldu");
      setCreateOpen(false);
      setForm({
        user_id: null, frequency_days: 30,
        next_run_at: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10),
        notes: "",
        items: [{ product_id: null, quantity: 1, price: 0 }]
      });
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (id) => {
    try {
      await API.put(`/api/admin/subscriptions/${id}/toggle`);
      toast.success("Durum güncellendi");
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  const cancel = async (id) => {
    if (!window.confirm("Bu abonelik iptal edilsin mi? (geri alınamaz)")) return;
    try {
      await API.delete(`/api/admin/subscriptions/${id}`);
      toast.success("İptal edildi");
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  const runNow = async (id) => {
    if (!window.confirm("Şimdi sipariş oluşturulsun mu? next_run_at değişmez.")) return;
    try {
      const r = await API.post(`/api/admin/subscriptions/${id}/run-now`);
      if (r.data?.success) {
        toast.success(`✓ Sipariş #${r.data.order_id} oluşturuldu (${fmtMoney(r.data.total)})`);
        load();
      } else {
        toast.error(r.data?.error || "Bilinmeyen hata");
      }
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  // KPI özeti
  const kpi = useMemo(() => ({
    total: subs.length,
    active: subs.filter(s => s.status === "active").length,
    paused: subs.filter(s => s.status === "paused").length,
    runs_total: subs.reduce((s, x) => s + Number(x.total_runs || 0), 0)
  }), [subs]);

  if (loading && subs.length === 0) {
    return <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={800}>Düzenli Sipariş Abonelikleri</Typography>
          <Typography variant="body2" color="text.secondary">
            Müşteri için "her X günde bir aynı sipariş" otomasyonu
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Yenile"><IconButton onClick={load}><RefreshIcon /></IconButton></Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            Yeni Abonelik
          </Button>
        </Box>
      </Box>

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

      {/* KPI */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="caption" color="text.secondary">Toplam</Typography>
            <Typography variant="h4" fontWeight={800}>{kpi.total}</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="caption" color="text.secondary">Aktif</Typography>
            <Typography variant="h4" fontWeight={800} color="#10b981">{kpi.active}</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="caption" color="text.secondary">Duraklatılmış</Typography>
            <Typography variant="h4" fontWeight={800} color="#f59e0b">{kpi.paused}</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="caption" color="text.secondary">Toplam Tetik</Typography>
            <Typography variant="h4" fontWeight={800} color="#1565C0">{kpi.runs_total}</Typography>
            <Typography variant="caption" color="text.disabled">otomatik sipariş</Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Subscription tablosu */}
      <Card sx={{ borderRadius: 3, overflow: "hidden" }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: "#f8fafc" }}>
            <TableRow>
              <TableCell><strong>Müşteri</strong></TableCell>
              <TableCell><strong>Sıklık</strong></TableCell>
              <TableCell><strong>Sonraki Çalışma</strong></TableCell>
              <TableCell align="center"><strong>Toplam Tetik</strong></TableCell>
              <TableCell align="center"><strong>Başarı/Hata</strong></TableCell>
              <TableCell align="center"><strong>Durum</strong></TableCell>
              <TableCell align="right"><strong>İşlem</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {subs.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  Henüz abonelik yok. <strong>"Yeni Abonelik"</strong> ile başla.
                </TableCell>
              </TableRow>
            )}
            {subs.map(s => {
              const sm = STATUS_META[s.status] || STATUS_META.cancelled;
              const isOverdue = new Date(s.next_run_at) < new Date() && s.status === "active";
              return (
                <TableRow key={s.id} hover sx={{ opacity: s.status === "cancelled" ? 0.5 : 1 }}>
                  <TableCell>
                    <Typography fontWeight={700} fontSize={13}>{s.company_name || s.user_name}</Typography>
                    <Typography variant="caption" color="text.disabled" fontSize={11}>{s.email}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontSize={13}>Her <strong>{s.frequency_days}</strong> gün</Typography>
                    <Typography variant="caption" color="text.disabled" fontSize={11}>
                      {FREQUENCY_PRESETS.find(p => p.days === Number(s.frequency_days))?.label || ""}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontSize={13} sx={{ color: isOverdue ? "#ef4444" : "inherit" }}>
                      {fmtDate(s.next_run_at)}
                      {isOverdue && " ⚠️"}
                    </Typography>
                    <Typography variant="caption" color="text.disabled" fontSize={11}>
                      Son: {fmtDate(s.last_run_at)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>{s.total_runs}</TableCell>
                  <TableCell align="center">
                    <Typography fontSize={11} color="#10b981">✓ {s.successful_runs || 0}</Typography>
                    {s.failed_runs > 0 && (
                      <Typography fontSize={11} color="#ef4444">✗ {s.failed_runs}</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={s.status === "active"}
                      onChange={() => toggleStatus(s.id)}
                      disabled={s.status === "cancelled"}
                      color="success"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Şimdi Çalıştır">
                      <IconButton size="small" onClick={() => runNow(s.id)} disabled={s.status === "cancelled"}
                        sx={{ color: "#3b82f6" }}>
                        <PlayArrowIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="İptal Et">
                      <IconButton size="small" onClick={() => cancel(s.id)} disabled={s.status === "cancelled"}
                        sx={{ color: "#ef4444" }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      {/* Yeni Abonelik Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <RepeatIcon /> Yeni Düzenli Sipariş Aboneliği
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={0}>
            <Grid item xs={12}>
              <Autocomplete
                options={users}
                value={users.find(u => u.id === form.user_id) || null}
                onChange={(_, v) => setForm({ ...form, user_id: v?.id || null })}
                getOptionLabel={(u) => `${u.company_name || u.name} (${u.email})`}
                isOptionEqualToValue={(o, v) => o.id === v?.id}
                renderInput={(params) => <TextField {...params} label="Müşteri *" />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select fullWidth label="Sıklık *" value={form.frequency_days}
                onChange={e => setForm({ ...form, frequency_days: parseInt(e.target.value, 10) })}
              >
                {FREQUENCY_PRESETS.map(p => (
                  <MenuItem key={p.days} value={p.days}>{p.label} ({p.days} gün)</MenuItem>
                ))}
                <MenuItem value={form.frequency_days}>Özel: {form.frequency_days} gün</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth type="date" label="İlk Çalışma Tarihi *"
                InputLabelProps={{ shrink: true }}
                value={form.next_run_at}
                onChange={e => setForm({ ...form, next_run_at: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Notlar (opsiyonel)" multiline rows={2}
                value={form.notes}
                onChange={e => setForm({ ...form, notes: e.target.value })}
                placeholder="Örn: 'Sipariş geldiğinde fatura kestir, kargo Yurtiçi olsun'"
              />
            </Grid>
          </Grid>

          {/* Items */}
          <Box sx={{ mt: 3, mb: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="subtitle1" fontWeight={700}>Ürünler ({form.items.length})</Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={addFormItem}>Ürün Ekle</Button>
          </Box>
          {form.items.map((item, i) => (
            <Card key={i} variant="outlined" sx={{ p: 1.5, mb: 1, bgcolor: "#fafbfc" }}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    size="small"
                    options={products}
                    value={products.find(p => p.id === item.product_id) || null}
                    onChange={(_, v) => updateFormItem(i, "product_id", v?.id || null)}
                    getOptionLabel={(p) => `${p.name}${p.sku ? ` [${p.sku}]` : ""}`}
                    isOptionEqualToValue={(o, v) => o.id === v?.id}
                    renderInput={(params) => <TextField {...params} label="Ürün *" />}
                  />
                </Grid>
                <Grid item xs={4} sm={2}>
                  <TextField
                    size="small" type="number" fullWidth label="Adet *"
                    value={item.quantity}
                    onChange={e => updateFormItem(i, "quantity", parseInt(e.target.value, 10) || 0)}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    size="small" type="number" fullWidth label="Birim Fiyat (₺)"
                    value={item.price}
                    onChange={e => updateFormItem(i, "price", parseFloat(e.target.value) || 0)}
                    helperText="Boş = güncel fiyat"
                  />
                </Grid>
                <Grid item xs={2} sm={1}>
                  {form.items.length > 1 && (
                    <IconButton size="small" onClick={() => removeFormItem(i)} sx={{ color: "#ef4444" }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Grid>
              </Grid>
            </Card>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={saving}>İptal</Button>
          <Button variant="contained" onClick={submitCreate} disabled={saving}>
            {saving ? "Kaydediliyor..." : "Aboneliği Oluştur"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
